import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "voyagr_token";
const USER_KEY = "voyagr_user";

// ── User persistence helpers (permettent l'accès hors-ligne) ──────────────────
function persistUser(u) {
  try { localStorage.setItem(USER_KEY, JSON.stringify(u)); } catch { /* quota */ }
}
function readStoredUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
}

// ── Token helpers — set/clear axios default header immediately ────────────────
export function setAxiosToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
}

export function clearAxiosToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  delete axios.defaults.headers.common["Authorization"];
}

// ── On module load: restore token from localStorage if present ────────────────
const storedToken = localStorage.getItem(TOKEN_KEY);
if (storedToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
}

export function AuthProvider({ children }) {
  // Restaure immédiatement l'utilisateur depuis localStorage si un token existe
  // → l'app reste accessible hors-ligne (pas de redirection vers /login).
  const [user, setUser] = useState(() => (storedToken ? readStoredUser() : null));
  const [loading, setLoading] = useState(true);

  const applyUser = (userInfo) => {
    setUser(userInfo);
    persistUser(userInfo);
  };

  // Tente de renouveler l'access_token via le cookie refresh_token
  const refreshAccessToken = async () => {
    try {
      const { data } = await axios.post(
        `${API}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      const { access_token, ...userInfo } = data;
      if (access_token) setAxiosToken(access_token);
      applyUser(userInfo);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      // Pas de token → utilisateur non connecté, inutile d'appeler l'API
      if (!storedToken) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        // Le SW renvoie {"offline":true} hors-ligne → conserver l'utilisateur restauré
        if (data.offline) return;
        const { access_token, ...userInfo } = data;
        if (access_token) setAxiosToken(access_token);
        applyUser(userInfo);
      } catch (error) {
        // Pas de réponse réseau = hors-ligne → conserver l'utilisateur restauré
        if (!error.response) return;
        // 401 → tenter un refresh avant de déconnecter
        if (error.response.status === 401) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            clearAxiosToken();
            setUser(null);
          }
        } else {
          clearAxiosToken();
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };
    checkAuth(); // eslint-disable-line react-hooks/exhaustive-deps
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password, remember_me = false) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password, remember_me }
    );
    const { access_token, ...userInfo } = data;
    setAxiosToken(access_token);
    applyUser(userInfo);
    return userInfo;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(
      `${API}/auth/register`,
      { email, password, name }
    );
    const { access_token, ...userInfo } = data;
    setAxiosToken(access_token);
    applyUser(userInfo);
    return userInfo;
  };

  const logout = async () => {
    await axios
      .post(`${API}/auth/logout`, {})
      .catch(() => {});
    clearAxiosToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { API };
